<script lang="ts">
import { PropType, defineComponent, h, resolveComponent, type VNode } from 'vue';

import { IAvailability } from '@/bundles/Availabilities/types';
import { getFormattedAddress } from '@/bundles/App/helpers/address/getFormattedAddress';
import { formatNumber } from '@/utils/filters';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';
import { getAccountRouteHandler } from '@/bundles/App/helpers';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

import AvailabilityChipsWrapper from '@/bundles/Availabilities/components/chips/AvailabilityChipsWrapper.vue';
import Label from '@/bundles/Common/components/Label.vue';
import BuildingSizeLabel from '@/bundles/MeasurementUnits/components/BuildingSizeLabel.vue';

export default defineComponent({
  name: 'AvailabilityMobileCard',

  props: {
    item: {
      type: Object as PropType<IAvailability>,
      required: true
    },
    activeAccountId: {
      type: Number,
      required: true
    }
  },

  render (): VNode | VNode[] {
    const VCard = resolveComponent('v-card');
    const VImg = resolveComponent('v-img');
    const RouterLink = resolveComponent('router-link');

    return h(VCard, {
      ...this.$attrs,
      variant: 'outlined',
      class: `availability-mobile-card ${(this.$attrs.class as string) || ''}`.trim(),
    }, { default: () => [
      h('div', { class: 'd-flex gap-2' }, [
        h(VImg, {
          src: this.item.photo_links?.medium_url || this.item?.photo?.url || (this as any).$placeholderPropertyImg,
          aspectRatio: 16 / 9,
          cover: false,
          position: 'center top',
          minWidth: '160px',
          maxWidth: '320px',
          class: 'availability-mobile-card__img'
        }, { default: () => [
          this.item.status_label ? h('div', { class: 'transaction-type-label' }, this.item.status_label) : null
        ] }),
        h('div', { class: 'availability-mobile-card__address-info px-3 pt-3' }, [
          h('div', { class: 'record-id' }, `Availability ID# ${this.item._key}`),
          h(RouterLink, {
            to: getAccountRouteHandler({
              name: 'availability',
              params: { id: this.item._key },
              activeAccountId: this.activeAccountId,
            }),
            class: 'font-weight-bold app-link availability-mobile-card__property-name'
          }, { default: () => this.item.property.name }),
          h('div', { class: 'white-space--pre-line availability-mobile-card__address' }, [
            getFormattedAddress({ ...this.item.address, street2: this.item.suite }, { includeStreet2: true })
          ])
        ])
      ]),
      h('div', { class: 'pa-3 pt-2 d-flex flex-column gap-2' }, [
        h(AvailabilityChipsWrapper, {
          agencyType: this.item.agency_type,
          inHouse: this.item.in_house,
          status: this.item.status,
          type: this.item.type,
          mixedUse: this.item.mixed_use,
          vacancyType: this.item.vacancy_type,
          isPortfolio: this.item.is_portfolio,
          buildingClass: this.item.property?.building?.class,
          contractPending: this.item.contract_pending,
          isPublishedToWeb: this.item.published_to_web.value,
          isOneLine: true,
          allowWrap: true
        }),
        h('div', { class: 'd-flex gap-2 flex-column' }, [
          h('div', { class: 'd-flex gap-3' }, [
            h('div', { class: 'availability-mobile-card__info' }, [
              h('div', { class: 'availability-mobile-card__info-label availability-mobile-card__available-space-label' }, 'Available Space:'),
              h('div', { class: 'availability-mobile-card__available-space' }, [
                h(BuildingSizeLabel, { value: this.item.available_space })
              ])
            ]),
            h('div', { class: 'availability-mobile-card__info' }, [
              h('div', { class: 'availability-mobile-card__info-label availability-mobile-card__divisible-space-label' }, 'Divisible Space:'),
              h('div', { class: 'availability-mobile-card__divisible-space' }, [
                h(BuildingSizeLabel, { value: this.item.divisible_space })
              ])
            ])
          ]),
          h('div', { class: 'd-flex gap-3' }, [
            h('div', { class: 'availability-mobile-card__info' }, [
              h('div', { class: 'availability-mobile-card__info-label availability-mobile-card__building-size-label' }, 'Building Size:'),
              h('div', { class: 'availability-mobile-card__building-size' }, [
                h(BuildingSizeLabel, { value: this.item.building_size })
              ])
            ]),
            h('div', { class: 'availability-mobile-card__info' }, [
              h('div', { class: 'availability-mobile-card__info-label availability-mobile-card__year-built-label' }, 'Year Built:'),
              h('div', { class: 'availability-mobile-card__year-built' }, [
                h(Label, { value: isValuePresent(this.item.property?.building?.year_built) ? formatNumber(this.item.property?.building?.year_built) : null })
              ])
            ])
          ]),
          h('div', { class: 'd-flex gap-3' }, [
            h('div', { class: 'availability-mobile-card__info' }, [
              h('div', { class: 'availability-mobile-card__info-label availability-mobile-card__construction-status-label' }, `${i18n.t(LocalizationEnum.construction_status)}:`),
              h('div', { class: 'availability-mobile-card__construction-status' }, [
                h(Label, { value: this.item.property?.building?.construction_status })
              ])
            ]),
            h('div', { class: 'availability-mobile-card__info' }, [
              h('div', { class: 'availability-mobile-card__info-label availability-mobile-card__vacancy-status-label' }, 'Vacancy Status:'),
              h('div', { class: 'availability-mobile-card__vacancy-status text-capitalize' }, [
                h(Label, { value: this.item.vacancy_status })
              ])
            ])
          ])
        ])
      ])
    ] });
  }
})
</script>

<style lang="scss" scoped>
.availability-mobile-card {
  .availability-mobile-card__img {
    border-top-left-radius: 3px; // outer border radius minus border width
    flex: 1 1 30%;
  }

  .availability-mobile-card__address-info {
    flex: 1 1 70%;
    line-height: 18px;
  }

  .availability-mobile-card__info {
    flex: 1 1 50%;
    line-height: 18px;
  }

  .availability-mobile-card__info-label {
    font-weight: 600;
  }
}
</style>
